import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Body2, EmphasisText, inlineTextIconMargin, InteractiveElement, sectionalMargin, StyledFieldLabel, styled, StyledIconButton, FillSpace, Header3 } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { EMPTY_GUID } from "@iventis/utilities";
import { Divider, LinearProgress, TextField, useTheme } from "@mui/material";
import React, { Fragment, useState } from "react";
import { ChipType, StyledChip } from "@iventis/components/src/chips";
import { CustomDialog, FormButtonsComponent, StatusIcon } from "@iventis/components";
import { SitemapVersionLevel } from "@iventis/domain-model/model/sitemapVersionLevel";
import { MapObjectSitemapLevelReassignmentPayload } from "@iventis/domain-model/model/mapObjectSitemapLevelReassignmentPayload";
import { SitemapVersionLevelStatus } from "@iventis/domain-model/model/sitemapVersionLevelStatus";
import { Theme } from "@emotion/react";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { useSitemapApiContext } from "./context/sitemap-api-context";
import { sortByLevelIndex } from "./sitemap-helpers";
import { useSitemapVersion } from "./hooks/use-sitemap-version";
import { useSitemapRoutingContext } from "./context/sitemap-routing-context";
import { StyledSitemapVersionRow } from "./sitemap-form";
import { LevelReassignmentForm } from "./sitemap-version-level-reassignment-form";
import { useSitemap } from "./hooks/use-sitemap";

export const SitemapVersionForm = () => {
    const translate = useIventisTranslate();

    const { setSelectedSitemapVersionId, setSelectedSitemapVersionLevelId } = useSitemapRoutingContext();

    const { sitemapVersion, setSitemapVersion, isSitemapVersionLoading, saveSitemapVersion, canSitemapVersionSave, isSaving, deleteSitemapVersionLevel } = useSitemapVersion();

    const { sitemap, isSitemapLoading } = useSitemap();

    const { reassignSitemapLevels } = useSitemapApiContext();

    const [sitemapVersionLevelForReassignment, setSitemapVersionLevelForReassignment] = useState<SitemapVersionLevel | null>();

    const theme = useTheme<Theme>();

    const onSitemapVersionLevelDeleted = async (sitemapVersionLevel: SitemapVersionLevel) => {
        const doesSitemapHaveAnotherLevelWithSameIndex = sitemapVersion.sitemapVersionLevels.some(
            ({ id, levelIndex }) => id !== sitemapVersionLevel.id && levelIndex === sitemapVersionLevel.levelIndex
        );

        if (!doesSitemapHaveAnotherLevelWithSameIndex) {
            setSitemapVersionLevelForReassignment(sitemapVersionLevel);
        } else {
            await deleteSitemapVersionLevel(sitemapVersionLevel.id);
        }
    };

    const handleLevelReassignment = async (reassignments: Omit<MapObjectSitemapLevelReassignmentPayload, "projectId">[]) => {
        const sitemapVersionLevelId = sitemapVersionLevelForReassignment.id;
        if (reassignments?.length > 0) {
            // Reassignment skipped if no reassignments are provided
            await reassignSitemapLevels(reassignments);
        }
        await deleteSitemapVersionLevel(sitemapVersionLevelId);
        setSitemapVersionLevelForReassignment(null);
    };

    const getAvailableSitemapVersions = () => [
        ...sitemap.versions.flatMap((version) => (version.id === sitemapVersion.id ? [] : [version])),
        {
            ...sitemapVersion,
            sitemapVersionLevels: sitemapVersion.sitemapVersionLevels.filter((svl) => svl.id !== sitemapVersionLevelForReassignment.id),
        },
    ];

    if (isSitemapVersionLoading || sitemapVersion == null) {
        return <LinearProgress />;
    }

    return (
        <>
            <Container>
                <StyledForm>
                    <div>
                        <Header3 className="header">
                            <span>{sitemap?.name}</span> /<span> </span>
                            {sitemapVersion?.name != null && sitemapVersion?.name !== "" ? (
                                <span>{sitemapVersion.name}</span>
                            ) : (
                                <span>{translate(Content.sitemaps.sitemapVersion.version)}</span>
                            )}
                        </Header3>
                        <StyledFieldLabel>{translate(Content.sitemaps.sitemapVersion.name)}</StyledFieldLabel>
                        <TextField
                            sx={{ minWidth: "70%" }}
                            inputProps={{ "data-testid": "sitemap-version-name-input" }}
                            variant="outlined"
                            value={sitemapVersion.name ?? null}
                            onChange={(event) => setSitemapVersion((s) => ({ ...s, name: event.target.value }))}
                        />
                    </div>
                    <div className="levels-header">
                        <InteractiveElement className="levels-sort" onClick={() => setSitemapVersion((s) => ({ ...s, sitemapVersionLevels: sortByLevelIndex(s) }))}>
                            <Body2>{translate(Content.sitemaps.sitemapVersion.levels)}</Body2>
                            <FontAwesomeIcon icon="sort" size="xs" />
                        </InteractiveElement>
                        <StyledChip
                            chipContainerClassName="add-level"
                            dataTestId="add-sitemap-level-button"
                            disabled={sitemapVersion.id === EMPTY_GUID}
                            label={
                                <>
                                    <FontAwesomeIcon icon={["far", "plus"]} /> {translate(Content.sitemaps.sitemapVersion.add_level)}
                                </>
                            }
                            chipType={ChipType.PrimaryButton}
                            onClick={() => setSelectedSitemapVersionLevelId(EMPTY_GUID)}
                        />
                    </div>
                    <div className="sitemap-version-level-container">
                        {!(sitemapVersion.sitemapVersionLevels?.length > 0) && <EmphasisText>{translate(Content.sitemaps.sitemapVersion.there_are_no_levels)}</EmphasisText>}
                        {sitemapVersion.sitemapVersionLevels
                            .sort((levelA, levelB) => levelA.levelIndex - levelB.levelIndex)
                            .map((level) => (
                                <Fragment key={level.id}>
                                    <Divider />
                                    <StyledSitemapVersionRow>
                                        <Body2>{level.levelName}</Body2>
                                        <FillSpace />
                                        <StatusIcon
                                            height={25}
                                            width={65}
                                            fill={
                                                level.status === SitemapVersionLevelStatus.Active
                                                    ? theme.secondaryColors.accept
                                                    : level.status === SitemapVersionLevelStatus.Failed
                                                    ? theme.secondaryColors.error
                                                    : theme.toastColours.info
                                            }
                                            label={translate(Content.sitemaps.sitemapVersion[level.status])}
                                        />
                                        <StyledIconButton
                                            disabled={isSitemapLoading}
                                            data-testid={`delete-sitemap-level-button-${level.levelName}`}
                                            onClick={() => onSitemapVersionLevelDeleted(level)}
                                        >
                                            <FontAwesomeIcon icon={["far", "trash"]} size="xs" />
                                        </StyledIconButton>
                                        <StyledIconButton
                                            data-testid={`edit-sitemap-version-level-button-${level.levelName}`}
                                            onClick={() => setSelectedSitemapVersionLevelId(level.id)}
                                            disabled={sitemapVersion.type === SitemapVersionType.ThreeDimensional}
                                        >
                                            <FontAwesomeIcon icon={["far", "pen-to-square"]} size="xs" />
                                        </StyledIconButton>
                                    </StyledSitemapVersionRow>
                                </Fragment>
                            ))}
                    </div>
                </StyledForm>
                <div className="form-buttons-container">
                    <FormButtonsComponent
                        handleSubmit={saveSitemapVersion}
                        disableSubmit={() => !canSitemapVersionSave}
                        isSubmitting={isSaving}
                        submitButtonText={translate(Content.common.buttons.save)}
                        secondaryButtons={[{ buttonText: translate(Content.common.buttons.back), onButtonPressed: () => setSelectedSitemapVersionId(null) }]}
                    />
                </div>
            </Container>
            <CustomDialog open={sitemapVersionLevelForReassignment != null}>
                {sitemapVersionLevelForReassignment != null && (
                    <LevelReassignmentForm
                        availableSitemapVersions={getAvailableSitemapVersions()}
                        existingLevelsRemoved={[sitemapVersionLevelForReassignment]}
                        confirm={handleLevelReassignment}
                        close={() => setSitemapVersionLevelForReassignment(null)}
                    />
                )}
            </CustomDialog>
        </>
    );
};

/*
    STYLES
*/

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    justify-content: space-between;

    .sitemap-version-level-container {
        display: flex;
        flex-direction: column;
        gap: 5px;
        flex: 1;
        overflow-y: auto;
    }

    .header {
        margin-bottom: 15px;
    }
    .form-buttons-container {
        display: flex;
    }
`;

const StyledForm = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: ${sectionalMargin};
    flex: 1;
    min-height: 0;

    .levels-header {
        display: flex;
        justify-content: space-between;
        align-items: end;

        .levels-sort {
            display: flex;
            column-gap: ${inlineTextIconMargin};
            align-items: center;
        }
    }
`;
